import { WidgetConfigBase } from '@src/types/Widget';
import { useMemo } from 'react';
import useGetDevicesFiltered from '../devices/useGetDevicesFiltered';
import useSearchWidgetConfigs from './useSearchWidgetConfigs';

export interface SimpleDevice {
  name: string;
  engineeringId: string;
  deviceId: string;
}

export interface DeviceWidgetOption extends SimpleDevice {
  widgetInternalId?: WidgetConfigBase['internalId'];
}

const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export default (deviceType: string, widgetInternalIdPrefix: string) => {
  const {
    data: devices,
    isLoading: isDevicesLoading,
    isFetching: isDevicesFetching,
    error: devicesError,
  } = useGetDevicesFiltered({
    body: {
      filter: {
        type: {
          $in: [deviceType],
        },
      },
    },
    options: {
      queryKeys: [deviceType],
    },
  });

  const {
    data: widgets,
    isLoading: isWidgetsLoading,
    isFetching: isWidgetsFetching,
    error: widgetsError,
  } = useSearchWidgetConfigs({
    filter: {
      isSystemWidget: true,
      internalId: { $regex: `^${escapeRegex(widgetInternalIdPrefix)}` },
    },
    enabled: !!devices?.length,
  });

  const deviceWidgetOptions: DeviceWidgetOption[] = useMemo(() => {
    if (!devices || !widgets) {
      return [];
    }

    // Create a map for O(1) lookup
    const widgetMap = new Map(widgets.map((w) => [w.internalId, w]));

    return devices.reduce<DeviceWidgetOption[]>((acc, device) => {
      const widgetKey = `${widgetInternalIdPrefix}${device.engineeringId}`;
      const widget = widgetMap.get(widgetKey);

      if (widget) {
        acc.push({
          name: device.name,
          engineeringId: device.engineeringId,
          deviceId: device.deviceId,
          widgetInternalId: widget.internalId,
        });
      }

      return acc;
    }, []);
  }, [devices, widgets, widgetInternalIdPrefix]);

  return {
    data: deviceWidgetOptions,
    isLoading: isDevicesLoading || isWidgetsLoading,
    isFetching: isDevicesFetching || isWidgetsFetching,
    error: devicesError || widgetsError,
  };
};
